import React from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';

const ONEDOC_URL = "https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid";

const CompulsionsSucre = () => {
  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title="Compulsions sucrées du soir — Nutritionniste Vevey, Clarens & Montreux"
        description="Envies de sucre incontrôlables le soir, grignotage, fringales ? Accompagnement personnalisé à Vevey, Clarens et Montreux pour comprendre et apaiser durablement les compulsions."
        keywords="compulsions sucre soir, fringales sucrées femme, grignotage Vevey, envie de sucre Clarens, nutritionniste compulsions Montreux Riviera"
        url="https://nutricentreriviera.ch/compulsions-sucre-soir"
      />

      {/* Hero */}
      <section style={{
        background: 'var(--gradient-hero)',
        padding: '5rem 0 4rem',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <span className="caption" style={{
            color: 'var(--accent-primary)',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '0.85rem',
            display: 'inline-block',
            marginBottom: '1rem'
          }}>
            Compulsions & comportement alimentaire
          </span>
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            Compulsions sucrées du soir : et si ce n'était pas qu'une question de volonté ?
          </h1>
          <p className="heading-subtitle" style={{ maxWidth: '780px', margin: '0 auto 2rem', lineHeight: 1.6 }}>
            Cette envie qui revient chaque soir. Ce besoin de sucre que vous ne contrôlez plus. Vous n'êtes pas seule — et ce n'est pas un défaut de discipline. À Vevey, Clarens et Montreux, nous vous accompagnons pour comprendre, apaiser et retrouver la sérénité.
          </p>
          <a href={ONEDOC_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ textDecoration: 'none' }} data-testid="compulsions-hero-cta">
            Prendre rendez-vous
            <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Vous vous reconnaissez */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            Vous vous reconnaissez si…
          </h2>
          <p className="body-large" style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: '2.5rem', lineHeight: 1.7 }}>
            Ces situations vous parlent ? Vous n'êtes pas seule. Elles reviennent dans presque toutes les consultations qui touchent au comportement alimentaire.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {[
              "Dès la fin du dîner, l'envie de sucre arrive — et vous savez déjà que vous allez craquer.",
              "Un carré de chocolat devient la tablette. Une mini-pâtisserie devient le paquet entier.",
              "Vous mangez sans avoir faim, par automatisme, par fatigue, par soulagement.",
              "Vous vous sentez coupable après, ce qui alimente encore le cycle.",
              "Le matin, vous tenez. À midi, ça va. Mais le soir, votre volonté s'effondre.",
              "Vous avez essayé la privation, les cheat days, les substituts « healthy » — sans résultat durable.",
              "Vous vous demandez si vous êtes addict au sucre.",
              "Les envies sont plus fortes avant les règles, en période de stress, ou quand vous êtes fatiguée."
            ].map((s, i) => (
              <div key={i} style={{
                display: 'flex',
                gap: '0.75rem',
                padding: '1.1rem 1.25rem',
                background: 'var(--bg-page)',
                borderRadius: '0.85rem',
                border: '1px solid var(--border-light)',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: 'var(--accent-primary)', flexShrink: 0, marginTop: '0.5rem'
                }} />
                <p className="body-medium" style={{ margin: 0, lineHeight: 1.55 }}>{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi c'est plus complexe */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
            Pourquoi les compulsions sucrées sont plus complexes qu'une question de volonté
          </h2>
          <p className="body-large" style={{ marginBottom: '1.5rem', lineHeight: 1.7 }}>
            La compulsion sucrée est un <strong>message</strong>, jamais un défaut de caractère. Derrière, plusieurs mécanismes peuvent être en jeu — souvent combinés :
          </p>
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {[
              ["Une glycémie instable dans la journée.", "Quelques repas trop sucrés ou trop pauvres en protéines suffisent à déclencher la fringale du soir."],
              ["Une résistance à l'insuline.", "Très fréquente et rarement diagnostiquée. Le cerveau reçoit mal l'énergie et réclame du sucre."],
              ["Des déséquilibres hormonaux.", "Œstrogènes, progestérone, cortisol, thyroïde — chacun peut amplifier les envies."],
              ["Un microbiote déréglé.", "Une prolifération de certaines levures peut littéralement « réclamer » du sucre."],
              ["Des carences en magnésium, chrome, zinc ou vitamines B.", "Souvent silencieuses, elles entretiennent les fringales."],
              ["Une dimension émotionnelle.", "Le sucre comble parfois un vide, une fatigue, un stress, ou un schéma enfoui depuis l'enfance."]
            ].map(([t, d], i) => (
              <li key={i} style={{
                background: 'white',
                border: '1px solid var(--border-light)',
                borderLeft: '4px solid var(--accent-primary)',
                borderRadius: '0.75rem',
                padding: '1.1rem 1.4rem'
              }}>
                <p className="body-medium" style={{ marginBottom: '0.35rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t}</p>
                <p className="body-medium" style={{ margin: 0, color: 'var(--text-muted)', lineHeight: 1.55 }}>{d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Pourquoi une approche personnalisée */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Pourquoi un accompagnement personnalisé change tout
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Tant que l'on ne comprend pas <strong>ce que le sucre vient combler dans votre cas</strong>, supprimer le sucre échoue presque toujours. Notre rôle est d'identifier ce qui se joue, et d'agir précisément à la racine.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {[
              ["Un bilan complet", "Anamnèse nutritionnelle et émotionnelle, bilan sanguin si nécessaire, analyse corporelle pour identifier les vraies causes."],
              ["Une stratégie sur mesure", "Stabilisation glycémique adaptée, ajustement des macronutriments, soutien du microbiote — sans frustration."],
              ["Une dimension émotionnelle", "Si le sucre joue un rôle de réconfort, l'hypnopraxie peut compléter la démarche pour libérer ce qui se joue en profondeur."]
            ].map(([t, d], i) => (
              <div key={i} style={{
                background: 'var(--bg-page)',
                borderRadius: '1rem',
                padding: '1.75rem',
                border: '1px solid var(--border-light)'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '0.75rem', color: 'var(--accent-primary)' }}>{t}</h3>
                <p className="body-medium" style={{ color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--accent-primary)', color: 'white', padding: '5rem 0', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: '720px' }}>
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
            Prête à apaiser ces envies ?
          </h2>
          <p className="body-large" style={{ color: 'white', opacity: 0.95, marginBottom: '2rem', lineHeight: 1.7 }}>
            Un premier rendez-vous permet de comprendre vos compulsions et de poser les bases d'un accompagnement bienveillant. Consultations remboursées par certaines assurances complémentaires (ASCA &amp; RME).
          </p>
          <a href={ONEDOC_URL} target="_blank" rel="noopener noreferrer" className="btn-primary"
             style={{ background: 'white', color: 'var(--accent-primary)', textDecoration: 'none' }}
             data-testid="compulsions-final-cta">
            Réserver ma consultation
            <ArrowRight size={20} />
          </a>
          <p className="caption" style={{
            marginTop: '1rem', color: 'white', opacity: 0.9,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
          }}>
            <CheckCircle2 size={16} />
            Cabinets à Vevey & Clarens (Montreux)
          </p>
        </div>
      </section>

      <RelatedPages items={relatedPagesMap['compulsions-sucre-soir']} />
    </div>
  );
};

export default CompulsionsSucre;
